import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "."
import { AdminSearchState } from "@/types/admin-search"

const abortControllers: Record<string, AbortController> = {}

const state: AdminSearchState = {
    users: [],
    locations: [],
    controllers: [],
    devices: [],
    loading: {
        users: false,
        locations: false,
        controllers: false,
        devices: false,
    },
}

const mutations: MutationTree<AdminSearchState> = {
    setUsers(state, users) {
        state.users = users
    },
    setLocations(state, locations) {
        state.locations = locations
    },
    setControllers(state, controllers) {
        state.controllers = controllers
    },
    setDevices(state, devices) {
        state.devices = devices
    },
    setLoading(
        state,
        { type, isLoading }: { type: keyof AdminSearchState["loading"]; isLoading: boolean },
    ) {
        state.loading[type] = isLoading
    },
}

const actions: ActionTree<AdminSearchState, RootState> = {
    async getUsers({ commit }, query?: string) {
        abortControllers.users?.abort()
        const controller = new AbortController()
        abortControllers.users = controller

        commit("setLoading", { type: "users", isLoading: true })

        try {
            const url = query ? `/search/users?${query}` : "/search/users"
            const response = await axios.get(url, { signal: controller.signal })

            commit("setUsers", response.data.data.users)
        } catch (error) {
            if (axios.isCancel(error)) return
            console.error("Failed to fetch users:", error)
        } finally {
            if (!controller.signal.aborted) {
                commit("setLoading", { type: "users", isLoading: false })
            }
        }
    },
    async getLocations({ commit }, query?: string) {
        abortControllers.locations?.abort()
        const controller = new AbortController()
        abortControllers.locations = controller

        commit("setLoading", { type: "locations", isLoading: true })

        try {
            const url = query ? `/search/locations?${query}` : "/search/locations"
            const response = await axios.get(url, { signal: controller.signal })

            commit("setLocations", response.data.data.locations)
        } catch (error) {
            if (axios.isCancel(error)) return
            console.error("Failed to fetch locations:", error)
        } finally {
            if (!controller.signal.aborted) {
                commit("setLoading", { type: "locations", isLoading: false })
            }
        }
    },
    async getControllers({ commit }, query?: string) {
        abortControllers.controllers?.abort()
        const controller = new AbortController()
        abortControllers.controllers = controller

        commit("setLoading", { type: "controllers", isLoading: true })

        try {
            const url = query ? `/search/controllers?${query}` : "/search/controllers"
            const response = await axios.get(url, { signal: controller.signal })

            commit("setControllers", response.data.data.controllers)
        } catch (error) {
            if (axios.isCancel(error)) return
            console.error("Failed to fetch controllers:", error)
        } finally {
            if (!controller.signal.aborted) {
                commit("setLoading", { type: "controllers", isLoading: false })
            }
        }
    },
    async getDevices({ commit }, query?: string) {
        abortControllers.devices?.abort()
        const controller = new AbortController()
        abortControllers.devices = controller

        commit("setLoading", { type: "devices", isLoading: true })

        try {
            const url = query ? `/search/devices?${query}` : "/search/devices"
            const response = await axios.get(url, { signal: controller.signal })

            commit("setDevices", response.data.data.devices)
        } catch (error) {
            if (axios.isCancel(error)) return
            console.error("Failed to fetch devices:", error)
        } finally {
            if (!controller.signal.aborted) {
                commit("setLoading", { type: "devices", isLoading: false })
            }
        }
    },
}

const getters: GetterTree<AdminSearchState, RootState> = {
    users: (state) => state.users,
    locations: (state) => state.locations,
    controllers: (state) => state.controllers,
    devices: (state) => state.devices,
    loading: (state) => state.loading,
}

const AdminSearchModule: Module<AdminSearchState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default AdminSearchModule
